import { ConnectedPosition } from '@angular/cdk/overlay'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxIconModule, FlxTooltipDirective } from '@flowx/angular-ui-toolkit'
import {
  TooltipTemplateConfig,
  accessibilityLabelOf,
  fonts,
  getSanitizedInlineMarkup,
  hasFocusableLink,
  localizeTemplateText,
  theme,
} from '@flowx/core-sdk'
import { ThemeComponent, applyTooltipCustomStyles, getComponentStyles } from '@flowx/core-theme'

import {
  getBoolExpressionSignal,
  getContentIcon,
  getDynamicPropSignal,
  getThemeIcon,
} from '../helpers'

const TOOLTIP_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -5,
  },
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 5,
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -5,
  },
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 5,
  },
]

@Component({
  selector: 'flx-tooltip-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxTooltipDirective, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './tooltip-wrapper.component.html',
  styleUrl: './tooltip-wrapper.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTooltipWrapperComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<TooltipTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  icons = computed(() => {
    const override = this.flowxProps()?.icon
    return {
      tooltip:
        (override?.value && getContentIcon(override)) ||
        getThemeIcon(ThemeComponent.Tooltip, 'tooltipIcon'),
    }
  })

  tooltipPosition: ConnectedPosition[] = TOOLTIP_POSITIONS

  styleClassName = signal('')

  themeClassName = computed(() => getComponentStyles(ThemeComponent.Tooltip, theme()) ?? '')
  linkThemeClassName = computed(() => getComponentStyles(ThemeComponent.Link, theme()) ?? '')

  triggerClass = computed(() =>
    `flx-tooltip-trigger ${this.displayOptions().className ?? ''} ${this.styleClassName()}`.trim()
  )

  popupClass = computed(() =>
    `flx-tooltip-popup-themed flx-themed ${this.themeClassName()} ${this.linkThemeClassName()} ${this.styleClassName()}`.trim()
  )

  isHidden: Signal<boolean>
  text: Signal<string>
  contentMarkup = computed(() => getSanitizedInlineMarkup(this.text()))
  ariaHidden: Signal<boolean>
  // Keyboard users reach the trigger, and from it the popup links, only when the text has a link.
  triggerTabIndex = computed(() => {
    if (this.ariaHidden()) {
      return -1
    }
    return hasFocusableLink(this.contentMarkup()) ? 0 : null
  })
  ariaLabel = computed(() =>
    localizeTemplateText(accessibilityLabelOf(this.flowxProps().accessibility))
  )

  constructor() {
    effect(async () => {
      const className = await applyTooltipCustomStyles(this.style(), theme(), fonts())
      this.styleClassName.set(className)
    })
  }

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { text, hide } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.ariaHidden = computed(() => {
        const accessibility = this.flowxProps().accessibility
        return accessibility && 'decorative' in accessibility ? accessibility.decorative : false
      })
      this.text = getDynamicPropSignal(text, this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
